// ─── I dati dell'anagrafica e del borsellino ─────────────────────────────────
//
// ⚖️ Lo stato del server NON si duplica in uno stato locale: TanStack Query è
// l'unica copia. Qui in particolare il saldo — due copie dello stesso saldo
// divergono, e quando divergono nessuno se ne accorge finché qualcuno non
// quadra la cassa.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AzzeraLivello,
  RigaStoricoDati,
  PremioDelSocio,
  AttivitaSocio,
  DisponibilitaDellaSegreteria,
  Borsellino,
  ElencoSoci,
  DomandaImportFile,
  EsitoImportFile,
  InvitoAlBot,
  TelegramDelSocio,
  ModificaSocio,
  MovimentoBorsellino,
  ScalaDeiLivelli,
  Socio,
  StoriaDelSocio,
  EsitoCancellazioneConStoria,
} from '@pmo/shared';
import { chiama, scarica } from '../../lib/api.js';
import { inEuro } from '../../lib/esiti.js';

export function usaSoci(filtro: {
  q: string;
  active?: boolean | undefined;
  archived: boolean;
  /** 🆕 Solo le schede nate dall'assistente. */
  fromAssistant?: boolean | undefined;
  /** 🙋 16/10/2026: solo le schede col sesso scritto dal bot, da controllare. */
  genderToCheck?: boolean | undefined;
  cursor?: string | null;
}) {
  const parametri = new URLSearchParams();
  if (filtro.q) parametri.set('q', filtro.q);
  if (filtro.active !== undefined) parametri.set('active', String(filtro.active));
  if (filtro.archived) parametri.set('archived', 'true');
  if (filtro.fromAssistant) parametri.set('fromAssistant', 'true');
  if (filtro.genderToCheck) parametri.set('genderToCheck', 'true');
  if (filtro.cursor) parametri.set('cursor', filtro.cursor);

  return useQuery({
    queryKey: [
      'soci',
      filtro.q,
      filtro.active,
      filtro.archived,
      filtro.fromAssistant ?? false,
      filtro.genderToCheck ?? false,
      filtro.cursor ?? '',
    ],
    queryFn: () => chiama<ElencoSoci>('GET', `/members?${parametri.toString()}`),
    /**
     * ⭐ LA RIGA CHE FA SEMBRARE VELOCE UNA RICERCA VELOCE. Senza, a ogni
     * battuta la lista si svuota e ricompare: il risultato arriva in 30 ms, ma
     * quello che si vede è un lampeggio. Tenendo i dati di prima finché non
     * arrivano quelli nuovi, la lista si **aggiorna** invece di rinascere.
     */
    placeholderData: (prima) => prima,
    staleTime: 15_000,
  });
}

/**
 * 🎾 La scala dei livelli del circolo, per leggerli e sceglierli a parole.
 * Cambia di rado — la si scrive dalle Impostazioni — quindi non si rilegge a
 * ogni scheda aperta.
 */
export const usaScalaDeiLivelli = () =>
  useQuery({
    queryKey: ['scala-livelli'],
    queryFn: () => chiama<ScalaDeiLivelli>('GET', '/members/livelli'),
    staleTime: 5 * 60_000,
  });

export const usaSocio = (id: string | null) =>
  useQuery({
    queryKey: ['socio', id],
    queryFn: () => chiama<Socio>('GET', `/members/${id}`),
    enabled: id !== null,
  });

export const usaAttivita = (id: string | null) =>
  useQuery({
    queryKey: ['attivita-socio', id],
    queryFn: () => chiama<AttivitaSocio>('GET', `/members/${id}/activity?limit=20`),
    enabled: id !== null,
  });

export const usaBorsellino = (id: string | null) =>
  useQuery({
    queryKey: ['borsellino', id],
    queryFn: () => chiama<Borsellino>('GET', `/wallet/${id}?limit=50`),
    enabled: id !== null,
  });

/** 🎁 I crediti premio di un socio (voce 33): a parte dal borsellino in euro. */
export const usaPremio = (id: string | null) =>
  useQuery({
    queryKey: ['premio', id],
    queryFn: () => chiama<PremioDelSocio>('GET', `/premio/${id}`),
    enabled: id !== null,
  });

/** Dopo ogni scrittura si rilegge: elenco, scheda e borsellino guardano gli stessi dati. */
function rileggiTutto(qc: ReturnType<typeof useQueryClient>, id: string): void {
  void qc.invalidateQueries({ queryKey: ['soci'] });
  void qc.invalidateQueries({ queryKey: ['socio', id] });
  void qc.invalidateQueries({ queryKey: ['borsellino', id] });
  void qc.invalidateQueries({ queryKey: ['attivita-socio', id] });
  void qc.invalidateQueries({ queryKey: ['storico-dati', id] });
  // 🎾 22/10/2026: un livello nuovo è una valutazione nuova per Level Akinator.
  void qc.invalidateQueries({ queryKey: ['valutazione-akinator', id] });
}

export function usaModificaSocio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'scheda del socio salvata' },
    mutationFn: (c: ModificaSocio) => chiama<Socio>('PATCH', `/members/${id}`, c),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/** 🙋 «✓ Controllato» (16/10/2026): il sesso scritto dal bot, guardato dalla Segreteria. */
/** 🙋 Il sesso a parole (16/10/2026): nella scheda e, dal 19/10, nella colonna Sesso dell'elenco. */
export const SESSO_A_PAROLE = { M: 'Uomo', F: 'Donna' } as const;

export function usaSessoControllato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'sesso controllato' },
    mutationFn: () => chiama<Socio>('POST', `/members/${id}/gender-checked`),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/**
 * 🗓️ Le disponibilità e i tipi di partita, dalla scheda (16/10/2026). Ogni tocco
 * si salva, come sul bot. La scheda che torna si mette subito al suo posto,
 * così il tocco dopo parte da quella salvata, e poi si rilegge il resto.
 */
export function usaDisponibilitaDelSocio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'disponibilità salvata' },
    mutationFn: (c: DisponibilitaDellaSegreteria) => chiama<Socio>('PUT', `/members/${id}/availability`, c),
    onSuccess: (s) => {
      qc.setQueryData(['socio', id], s);
      rileggiTutto(qc, id);
    },
  });
}

export function usaCreazioneSocio() {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: (d) => `socio ${(d as Socio).displayName} creato` },
    mutationFn: (c: Record<string, unknown>) => chiama<Socio>('POST', '/members', c),
    onSuccess: (s) => rileggiTutto(qc, s.id),
  });
}

export function usaArchiviazione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: (_d, v) => (v === 'archive' ? 'socio archiviato' : 'socio riportato fra gli attivi') },
    mutationFn: (verso: 'archive' | 'restore') => chiama<Socio>('POST', `/members/${id}/${verso}`),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/**
 * 🗑️ La storia del socio, chiesta quando si tocca «Cancella» (28/09/2026, scelta 2A): dice se la
 * scheda si può cancellare o se va archiviata. Si rilegge ogni volta, mai tenuta.
 */
export const usaStoriaDelSocio = (id: string, chiesta: boolean) =>
  useQuery({
    queryKey: ['storia-socio', id],
    queryFn: () => chiama<StoriaDelSocio>('GET', `/members/${id}/deletion`),
    enabled: chiesta,
    staleTime: 0,
  });

/** 🛡️ Se il socio ha chiesto dal bot di cancellare i suoi dati (22/10/2026), e quando. */
export const usaCancellazioneChiesta = (id: string) =>
  useQuery({
    queryKey: ['cancellazione-chiesta', id],
    queryFn: () => chiama<{ requestedAt: string | null }>('GET', `/members/${id}/erasure-request`),
    staleTime: 0,
  });

/**
 * 🧪 «Azzera il livello», solo per le prove (14/10/2026): se il bottone c'è lo dice il gestionale
 * (il titolare, la scheda di Maurizio Aprea, Marco Natalini o Luca D’Abruzzo, il bot di prova).
 */
export const usaAzzeraLivelloDisponibile = (id: string) =>
  useQuery({
    queryKey: ['azzera-livello', id],
    queryFn: () => chiama<AzzeraLivello>('GET', `/members/${id}/level-reset`),
    staleTime: 0,
  });

export function usaAzzeraLivello(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'livello azzerato' },
    mutationFn: () => chiama<{ reset: true }>('POST', `/members/${id}/level-reset`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['socio', id] });
      void qc.invalidateQueries({ queryKey: ['soci'] });
    },
  });
}

/**
 * 🧪 «Prepara le notifiche di prova» (30/10/2026, mockup «Le notifiche di prova», A): stesse chiavi di «Azzera il
 * livello». Sul telefono del titolare arriva la sezione delle notifiche, con partite e lezioni di prova.
 */
export function usaNotificheDiProva(id: string) {
  return useMutation({
    meta: { fatto: 'notifiche di prova preparate' },
    mutationFn: () => chiama<{ prepared: true }>('POST', `/members/${id}/demo-notices`),
  });
}

export function usaCancellazioneSocio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'scheda del socio cancellata' },
    mutationFn: () => chiama<{ deleted: true }>('DELETE', `/members/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['soci'] });
      qc.removeQueries({ queryKey: ['socio', id] });
      qc.removeQueries({ queryKey: ['storia-socio', id] });
    },
  });
}

/**
 * 🗑️ Il titolare cancella anche una scheda CON la storia (29/09/2026, mockup «Cancellare un socio»:
 * 1A · solo il titolare · conferma col nome). Via la persona, restano i conti a nome «Socio
 * cancellato»: si rileggono i soci, e anche la Cassa e il calendario che lo nominano.
 */
export function usaCancellazioneConStoria(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'scheda del socio cancellata, restano i conti' },
    mutationFn: (confirmName: string) =>
      chiama<EsitoCancellazioneConStoria>('POST', `/members/${id}/erase`, { confirmName }),
    onSuccess: () => {
      void qc.invalidateQueries();
      qc.removeQueries({ queryKey: ['socio', id] });
      qc.removeQueries({ queryKey: ['storia-socio', id] });
    },
  });
}

/**
 * 🚨 LA CHIAVE DI IDEMPOTENZA LA GENERA CHI APRE LA FINESTRA, non chi preme.
 *
 * ⚖️ È qui che si vince il criterio della Fase 5 («si preme due volte ricarica
 * e il movimento resta UNO»). Generandola al momento del clic, due clic
 * farebbero due chiavi e quindi due movimenti — e il bottone disabilitato non
 * basta: non ferma un `Invio` ripetuto, non ferma una rete che rimanda la
 * richiesta, e non ferma due schede aperte sullo stesso socio.
 * 📌 L'unica difesa che regge è che i due tentativi portino **la stessa
 *    chiave**, e che a rifiutare il secondo sia il database.
 */
export function usaMovimentoBorsellino(id: string, chiave: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (_d, v) => {
        const m = v as { verso: 'recharge' | 'withdraw'; amountCents: number };
        return m.verso === 'recharge'
          ? `ricarica di ${inEuro(m.amountCents)} registrata nel borsellino`
          : `tolti ${inEuro(m.amountCents)} dal borsellino`;
      },
    },
    mutationFn: (m: {
      verso: 'recharge' | 'withdraw';
      amountCents: number;
      reason: string | null;
      /**
       * 💶 Con che mezzo il denaro è passato dal cassetto (Fase 6). `null` =
       * rettifica: il borsellino si muove e la cassa non se ne accorge.
       */
      method: 'cash' | 'card' | null;
    }) =>
      chiama<MovimentoBorsellino>('POST', `/wallet/${id}/${m.verso}`, {
        amountCents: m.amountCents,
        reason: m.reason,
        method: m.method,
        idempotencyKey: chiave,
      }),
    // 💶 Una ricarica in contanti è anche una riga di cassa: il riepilogo della
    // giornata e l'elenco degli incassi vanno riletti, o resterebbero a dire
    // una cifra di un minuto fa.
    onSuccess: () => {
      rileggiTutto(qc, id);
      void qc.invalidateQueries({ queryKey: ['incassi'] });
      void qc.invalidateQueries({ queryKey: ['riepilogo-cassa'] });
    },
  });
}

export function usaStornoMovimento(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'movimento del borsellino stornato' },
    mutationFn: (transactionId: string) =>
      chiama<MovimentoBorsellino>('POST', `/wallet/${id}/reverse`, { transactionId }),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/**
 * 📥 Voce 26: l'importazione dalla rubrica Google e da Matchpoint. Con
 * `apply: false` legge e basta; con `apply: true` scrive le righe scelte, e
 * allora l'elenco dei soci va riletto.
 */
export function usaImportFile() {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      // L'anteprima (senza `apply`) non cambia niente: tace.
      fatto: (d) => {
        const e = d as EsitoImportFile;
        return e.applicato ? `importazione fatta: ${e.creati} soci nuovi, ${e.aggiornati} aggiornati` : null;
      },
    },
    mutationFn: (d: Pick<DomandaImportFile, 'file' | 'scelte'> & { apply: boolean }) =>
      chiama<EsitoImportFile>('POST', '/members/import/file', d),
    onSuccess: (esito) => {
      if (esito.applicato) void qc.invalidateQueries({ queryKey: ['soci'] });
    },
  });
}

/**
 * L'export.
 *
 * ⛔ NON è un `<a href>`: il gettone di accesso sta in memoria, non in un
 * cookie, quindi un download avviato dal browser tornerebbe un 401. Passa da
 * `scarica()`, che mette l'intestazione al posto giusto e poi consegna il file.
 */
export function esporta(
  formato: 'csv' | 'xlsx',
  filtro: { q: string; archived: boolean; fromAssistant?: boolean; genderToCheck?: boolean },
): Promise<void> {
  const p = new URLSearchParams({ format: formato });
  if (filtro.q) p.set('q', filtro.q);
  if (filtro.archived) p.set('archived', 'true');
  if (filtro.fromAssistant) p.set('fromAssistant', 'true');
  if (filtro.genderToCheck) p.set('genderToCheck', 'true');
  return scarica(`/members/export?${p.toString()}`, `soci.${formato}`);
}

// ── Il bot dei soci nella scheda (24/09/2026; link unico dalla voce 28) ─────

export const usaInvitoBot = (id: string) =>
  useQuery({
    queryKey: ['invito-bot', id],
    queryFn: () => chiama<InvitoAlBot>('GET', `/members/${id}/bot-invite`),
  });

/** 🕘 B1 · 3A (29/09/2026): nome, cognome e numero, prima → dopo, e chi. */
export const usaStoricoDeiDati = (id: string) =>
  useQuery({
    queryKey: ['storico-dati', id],
    queryFn: () => chiama<RigaStoricoDati[]>('GET', `/members/${id}/data-history`),
  });

/** 🔒 A7 (28/09/2026): gli account Telegram della scheda, vivi e scollegati. */
export const usaTelegramDelSocio = (id: string) =>
  useQuery({
    queryKey: ['telegram-socio', id],
    queryFn: () => chiama<TelegramDelSocio>('GET', `/members/${id}/telegram`),
  });

export function usaScollegaTelegram(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'account Telegram scollegato' },
    mutationFn: (v: { linkId: string; reason: string }) =>
      chiama<TelegramDelSocio>('POST', `/members/${id}/telegram/${v.linkId}/unlink`, { reason: v.reason }),
    onSuccess: (dopo) => {
      qc.setQueryData(['telegram-socio', id], dopo);
      void qc.invalidateQueries({ queryKey: ['soci'] });
    },
  });
}
